import OrderFinancialBreakdown from 'src/components/shared/OrderFinancialBreakdown'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Button, Checkbox, Divider, Popconfirm, Table, Tooltip } from 'antd'
import { convertArrayToCSV } from 'convert-array-to-csv'
import * as dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CSVLink } from 'react-csv'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import OMSLogo from 'src/assets/images/OMSLogoWht.png'
import { BodyText, H1, H3, H4 } from 'src/branding/typography'
import FadeIn from 'src/components/styled/FadeIn'
import { SpaceBetween } from 'src/components/styled/FlexBox'
import LoadingButton from 'src/components/styled/LoadingButton'
import {
  fetchOrdersChecklist,
  checklistScopeKey,
  lockOrder,
  // notifications,
  supplierNotifications,
} from 'src/features/ChecklistSlice'
import { getVenue } from 'src/features/VenueSlice'
import { editVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import TableColumnsCheckList from 'src/screens/order-checklist/TableColumnsChecklist'
import { findUnique } from 'src/utils/arrayFunctions'
import GetWeekDay from 'src/utils/getWeekDays'
import {
  getTotalSpendReceiptingWithVAT,
  getTotalSpendReceipting,
} from 'src/utils/priceFunctions'
import {
  ORDER_CHECKLIST_CSV_HEADERS,
  buildOrderChecklistCsvData,
} from 'src/utils/orderChecklistCsv'
import { useWindowWidth, windowSize } from 'src/utils/useWindowWidth'
import styled from 'styled-components/macro'
import Talk from 'talkjs'
import {
  ensureTalkJsToastDoesNotCoverNav,
  TALKJS_POPUP_CHROME,
} from 'src/utils/purchasingChrome'
import { createNewReceipting } from '../../features/ReceiptingSlice'
import safeDecode from 'src/utils/safeDecode'
import { SalesAdvisorMark } from 'src/components/icons/SalesAdvisorIcon'
import SalesAdvisorModal from 'src/screens/overview/customers/SalesAdvisorModal'
import SupplierOrderTransmission from './SupplierOrderTransmission'
import ChecklistAccessGate from './ChecklistAccessGate'
import Sentry from 'src/utils/sentry'
import {
  SALES_ADVISOR_CHECKLIST_BODY,
  SALES_ADVISOR_CHECKLIST_CTA,
  SALES_ADVISOR_CHECKLIST_HEADLINE,
} from 'src/screens/overview/customers/salesAdvisorCopy'

dayjs.extend(advancedFormat)

const OrderChecklist = props => {
  const dispatch = useDispatch()
  const { isLoading: isLoadingVenue } = useSelector(
    state => state.venue.isLoading,
  )
  const {
    // venueDeliveryDays,
    addressLine1,
    addressLine2,
    addressCity,
    addressPostCode,
    phoneNumber,
    title,
  } = useSelector(state => state.venue.venue) || {}
  const isAdmin = useSelector(state => state.auth.account.isAdmin || null)
  const viewerId = useSelector(state => state.auth.account?._id || '')
  const isSupplierAdmin = useSelector(state =>
    Boolean(state.auth.account?.supplierAdmin),
  )
  const {
    venueSupplier,
    venueContact: venueContactFromChecklist,
    approvedOrders,
    noApprovedOrders: noApprovedOrdersFromServer,
    locked,
    orderError,
    isLoading: isLoadingChecklist,
    scopeKey,
    viewerId: checklistViewerId,
    accessError,
    isIntegratedSupplier,
  } = useSelector(state => state.checklist)
  const isSubstitutedColumnShown = approvedOrders.some(
    currentOrder => currentOrder.supplier?.integrationType === 'Brakes',
  )
  const {
    accountNumber,
    supplierTitle,
    hasVenueUpdateAddress,
    supplier,
    venue,
    // hasVenueUpdateDeliveryDays,
  } = venueSupplier || {}

  const { venueId, supplierId, date, orderNumber = 'all' } = props.match.params
  const isLoading = isLoadingVenue || isLoadingChecklist

  const decodedOrderNumber = safeDecode(orderNumber)
  const scopeMatches =
    scopeKey ===
      checklistScopeKey(venueId, supplierId, date, decodedOrderNumber) &&
    checklistViewerId === viewerId
  const checklistToken =
    typeof URLSearchParams === 'undefined'
      ? ''
      : new URLSearchParams(props.location?.search || '').get('sat') || ''

  const [noApprovedOrders, setNoApprovedOrders] = useState([])
  const [confirmingOrder, setConfirmingOrder] = useState(false)
  const [noApprovedTotal, setNoApprovedTotal] = useState(0)
  const [approvedTotal, setApprovedTotal] = useState(0)
  const [vatTotalForAprov, setVatTotalForAprov] = useState(0)
  const [vatTotalForNoAprov, setVatTotalForNoAprov] = useState(0)
  const [totalAmountForAprov, setTotalAmountForAprov] = useState(0)
  const [totalAmountForNoAprov, setTotalAmountForNoAprov] = useState(0)
  const [areEveryOrderConfirmed, setAreEveryOrderConfirmed] = useState(false)
  const [chatButton, setChatButton] = useState(true)
  const [editable, setEditable] = useState(false)
  const [isManuallyAdded, setIsManuallyAdded] = useState(false)
  const [salesAdvisorVisible, setSalesAdvisorVisible] = useState(false)

  const completed = noApprovedOrders.length === 0 || locked

  const [receiptingState, setReceiptingState] = useState([])

  const layout = windowSize(useWindowWidth())
  const buttonSize =
    layout === 'md' || layout === 'sm' || layout === 'xs' ? 'default' : 'large'

  const orderDate = date

  const displayDate = `${GetWeekDay(dayjs(orderDate).get('day'))}  ${dayjs(
    orderDate,
  )
    .format('DD/MM/YYYY')
    .split('/')
    .join(' / ')}`

  const notes = findUnique(
    [...noApprovedOrders, ...approvedOrders]
      .filter(
        order =>
          order.notes !== undefined &&
          order.notes !== null &&
          order.notes !== '',
      )
      .map(order => ({ notes: order.notes })),
    order => order.notes,
    'values',
  )

  const requiredDeliveryDays = [...noApprovedOrders, ...approvedOrders]
    .filter(
      order =>
        order.requiredDeliveryDay !== null &&
        order.requiredDeliveryDay !== undefined,
    )
    .map(order => ({ requiredDeliveryDay: order.requiredDeliveryDay }))

  const requiredDeliveryDayToShow =
    requiredDeliveryDays &&
    requiredDeliveryDays.length > 0 &&
    requiredDeliveryDays[0].requiredDeliveryDay
      ? dayjs(
          requiredDeliveryDays[requiredDeliveryDays.length - 1]
            .requiredDeliveryDay,
        ).format('dddd Do MMMM YYYY')
      : 'Next available delivery day'

  const csvOrderDate = dayjs(orderDate).format('DD-MM-YYYY')
  const csvDeliveryDate =
    requiredDeliveryDays &&
    requiredDeliveryDays.length > 0 &&
    requiredDeliveryDays[0].requiredDeliveryDay
      ? dayjs(
          requiredDeliveryDays[requiredDeliveryDays.length - 1]
            .requiredDeliveryDay,
        ).format('DD-MM-YYYY')
      : dayjs(orderDate).add(1, 'day').format('DD-MM-YYYY')

  const csvData = useRef([])
  const csvLink = useRef(null)

  useEffect(() => {
    let active = true
    dispatch(
      fetchOrdersChecklist(
        venueId,
        supplierId,
        date,
        decodedOrderNumber,
        Boolean(locked),
      ),
    )
      .then(result => {
        if (active && result.meta.requestStatus === 'fulfilled')
          return dispatch(getVenue(venueId))
        return undefined
      })
      .catch(error => Sentry.captureException(error))
    return () => {
      active = false
    }
  }, [
    dispatch,
    venueId,
    supplierId,
    date,
    decodedOrderNumber,
    viewerId,
    locked,
  ])

  useEffect(() => {
    if (orderError) setConfirmingOrder(false)
  }, [orderError])

  useEffect(() => {
    if (approvedOrders.length > 0) {
      const total = getTotalSpendReceipting(approvedOrders)
      setApprovedTotal(total)
      const totalwithVAt = getTotalSpendReceiptingWithVAT(approvedOrders)
      setVatTotalForAprov(totalwithVAt.totalVAT || 0)
      setTotalAmountForAprov(totalwithVAt.totalSpend || 0)
    }
  }, [approvedOrders])

  useEffect(() => {
    if (approvedOrders.length > 0 && receiptingState.length > 0) {
      const updatedApprovedOrders = approvedOrders.map(order => {
        const findReceipting = receiptingState.find(
          receipting => order._id === receipting.order,
        )
        return findReceipting
          ? {
              ...order,
              receipting: order?.receipting
                ? { ...order?.receipting, ...findReceipting }
                : findReceipting,
            }
          : order
      })

      const total = getTotalSpendReceipting(updatedApprovedOrders)
      setApprovedTotal(total)
      const totalwithVAt = getTotalSpendReceiptingWithVAT(updatedApprovedOrders)
      setVatTotalForAprov(totalwithVAt.totalVAT || 0)
      setTotalAmountForAprov(totalwithVAt.totalSpend || 0)
    }
  }, [receiptingState])

  useEffect(() => {
    setNoApprovedOrders([...noApprovedOrdersFromServer])
    if (noApprovedOrdersFromServer.length > 0) {
      window.onbeforeunload = () => true
    } else {
      window.onbeforeunload = undefined
    }
  }, [noApprovedOrdersFromServer])

  useEffect(() => {
    if (noApprovedOrders) {
      const total = getTotalSpendReceipting(noApprovedOrders)
      setNoApprovedTotal(total)
      const totalwithVAt = getTotalSpendReceiptingWithVAT(noApprovedOrders)
      setVatTotalForNoAprov(totalwithVAt.totalVAT || 0)
      setTotalAmountForNoAprov(totalwithVAt.totalSpend || 0)
      setAreEveryOrderConfirmed(
        noApprovedOrders.every(order => order.supplierConfirmed !== undefined),
      )
    }
  }, [noApprovedOrders])

  const talkhandle = async () => {
    Talk.ready
      .then(async function () {
        const me = new Talk.User({
          id: supplier,
          name: supplierTitle,
          welcomeMessage: 'Hey there! How are you? :-)',
          role: 'default',
        })
        const other = new Talk.User({
          id: venue,
          name: title,
          welcomeMessage: 'Hey there! Send us a message to start a chat 😁:',
          role: 'venues',
        })
        window.talkSession = new Talk.Session({
          appId: 'GR4DzdvG',
          me: me,
        })
        const conversation = window.talkSession.getOrCreateConversation(
          Talk.oneOnOneId(me, other),
        )
        conversation.setParticipant(me)
        conversation.setParticipant(other)
        ensureTalkJsToastDoesNotCoverNav()
        const popup = window.talkSession.createPopup(
          conversation,
          TALKJS_POPUP_CHROME,
        )
        popup.onSendMessage(() => {
          // ... make backend request here
          try {
            supplierNotifications(venue, supplier)
          } catch (e) {
            console.log(e)
          }
        })
        popup.select(conversation)
        await popup.mount({ show: true })
        setChatButton(false)
        return
      })
      .catch(err => console.log(err))
  }

  // Prefer the limited business contact block from the checklist response;
  // fall back to venue details already on screen for older API payloads
  const contactDetails = venueContactFromChecklist || {
    title,
    phoneNumber,
    addressLine1,
    addressLine2,
    addressCity,
    addressPostCode,
  }
  const contactPhoneNumber = contactDetails?.phoneNumber || null
  const contactEmailAddress = contactDetails?.emailAddress || null

  const [contactDetailsCopied, setContactDetailsCopied] = useState(false)

  const copyContactDetails = async () => {
    const lines = [
      contactDetails?.title,
      contactDetails?.addressLine1,
      contactDetails?.addressLine2,
      contactDetails?.addressCity,
      contactDetails?.addressPostCode,
      contactPhoneNumber,
      contactEmailAddress,
    ].filter(Boolean)
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      setContactDetailsCopied(true)
      setTimeout(() => setContactDetailsCopied(false), 2000)
    } catch (e) {
      console.error(e)
    }
  }

  const completeOrder = () => {
    setConfirmingOrder(true)
    dispatch(lockOrder(venueId, supplierId, noApprovedOrders, orderError))
    dispatch(
      editVenueSuppliers({
        venue: venueId,
        hasVenueUpdateAddress: false,
        hasVenueUpdateDeliveryDays: false,
      }),
    )
  }

  const hanldeApproveAll = event => {
    const { checked } = event.target
    setNoApprovedOrders([
      ...noApprovedOrders.map(order => ({
        ...order,
        supplierConfirmed: checked || undefined,
      })),
    ])
  }

  // const renderVenueDeliveryDays = () => (
  //   <VenueDeliveryDaysHolder>
  //     <DeliveryDaysRow>
  //       <H4>Delivery availability:</H4>
  //       {hasVenueUpdateDeliveryDays && (
  //         <ErrorTag>
  //           <ExclamationCircleOutlined
  //             style={{
  //               fontSize: 17,
  //               paddingRight: 4,
  //             }}
  //           />
  //           Delivery days have been updated
  //         </ErrorTag>
  //       )}
  //     </DeliveryDaysRow>
  //     <p>
  //       Please deliver at the earliest opportunity, on one of the venues
  //       available days.
  //     </p>
  //     <DeliveryDays>
  //       {Object.entries(venueDeliveryDays).map(([day, value]) =>
  //         value ? <DayBox>{wordCaps(day).slice(0, 3)}</DayBox> : null,
  //       )}
  //     </DeliveryDays>
  //   </VenueDeliveryDaysHolder>
  // )

  const renderApprovedButton = () => (
    <ApprovedButton
      size={buttonSize}
      loading={confirmingOrder && !locked}
      disabled={!areEveryOrderConfirmed || completed}
      completed={completed ? 1 : 0}
      block
    >
      {completed ? 'Order completed' : 'Approve Order'}
    </ApprovedButton>
  )

  const renderEditButton = () => {
    return (
      <EditButton size={buttonSize} block onClick={() => setEditable(true)}>
        Edit Checklist
      </EditButton>
    )
  }

  const renderSaveButton = () => {
    return (
      <Popconfirm
        title='Are you sure you want to confirm this changes?'
        placement='topRight'
        onConfirm={async e => {
          e.stopPropagation()
          setEditable(false)
          const newStateByLater = receiptingState.map(receipt => {
            const findOrder = approvedOrders.find(
              order =>
                order.receipting &&
                order.receipting.error ===
                  'Adjustment due to Invoice received' &&
                receipt.order === order._id,
            )

            return findOrder
              ? { ...findOrder.receipting, ...receipt, _id: undefined }
              : receipt
          })

          await dispatch(
            createNewReceipting(newStateByLater, {
              venueId,
              supplierId,
              PONumber: orderNumber,
            }),
          )
          dispatch(fetchOrdersChecklist(venueId, supplierId, date, orderNumber))
        }}
        onCancel={e => {
          e.stopPropagation()
        }}
        okText='Yes'
        cancelText='No'
      >
        <EditButton size={buttonSize} block>
          Save
        </EditButton>
      </Popconfirm>
    )
  }

  useEffect(() => {
    // Current checklist state: pending lines first, then approved — same columns.
    // Building the CSV is read-only; download must not approve or mutate orders.
    const data = buildOrderChecklistCsvData({
      pendingOrders: noApprovedOrders,
      approvedOrders,
      pendingTotal: noApprovedTotal,
      approvedTotal,
      accountNumber,
      orderDate: csvOrderDate,
      deliveryDate: csvDeliveryDate,
      poNumber: decodedOrderNumber,
      venueId,
      supplierId,
    })

    csvData.current = convertArrayToCSV(data, {
      header: ORDER_CHECKLIST_CSV_HEADERS,
    })
  }, [
    approvedOrders,
    noApprovedOrders,
    approvedTotal,
    noApprovedTotal,
    accountNumber,
    csvOrderDate,
    csvDeliveryDate,
    decodedOrderNumber,
  ])

  // Download only triggers the hidden CSVLink — never lockOrder / completeOrder.
  const downloadCsv = useCallback(() => {
    csvLink.current?.link?.click()
  }, [])

  useEffect(() => {
    if (approvedOrders[0]) {
      approvedOrders[0].placedVia === 'Order placed manually on the dashboard'
        ? setIsManuallyAdded(true)
        : setIsManuallyAdded(false)
    }
  }, [approvedOrders])

  return (
    <ChecklistAccessGate
      scopeMatches={scopeMatches}
      error={accessError}
      isLoading={isLoading}
    >
      {noApprovedOrders.length === 0 && approvedOrders.length === 0 ? (
        <Wrapper>
          <CenterScreen>
            <H4>There are currently no orders attributed to this checklist.</H4>
            <H4>Please check with OMS to see if there has been an error.</H4>
          </CenterScreen>
        </Wrapper>
      ) : (
        <Wrapper>
          <Helmet>
            <title>Order checklist</title>
          </Helmet>

          <Header>
            <HeaderContent>
              <LeftHeader>
                {noApprovedOrders.length > 0 ? (
                  <BodyText>
                    Hey {supplierTitle}, you&apos;ve received a new order!
                  </BodyText>
                ) : (
                  <BodyText>
                    Hey {supplierTitle}, here are today&apos;s accepted orders!
                  </BodyText>
                )}
                <VenueName>{title}</VenueName>
                {accountNumber && (
                  <BodyText>Account number: {accountNumber}</BodyText>
                )}
              </LeftHeader>
              <Logo src={OMSLogo} alt='Orders Made Simple logo' />
            </HeaderContent>
          </Header>

          {isSupplierAdmin && isIntegratedSupplier && (
            <Inner>
              <SupplierOrderTransmission
                key={`${supplierId}-${venueId}-${date}-${decodedOrderNumber}`}
                supplierId={supplierId}
                venueId={venueId}
                date={date}
                purchaseOrderNumber={decodedOrderNumber}
              />
            </Inner>
          )}

          <SalesAdvisorBanner>
            <SalesAdvisorBannerInner>
              <SalesAdvisorMark size={48} />
              <SalesAdvisorBannerCopy>
                <SalesAdvisorHeadline>
                  ✨ {SALES_ADVISOR_CHECKLIST_HEADLINE}
                </SalesAdvisorHeadline>
                <SalesAdvisorBody>
                  {SALES_ADVISOR_CHECKLIST_BODY}
                </SalesAdvisorBody>
              </SalesAdvisorBannerCopy>
              <SalesAdvisorCta
                type='button'
                onClick={() => setSalesAdvisorVisible(true)}
              >
                {SALES_ADVISOR_CHECKLIST_CTA}
              </SalesAdvisorCta>
            </SalesAdvisorBannerInner>
          </SalesAdvisorBanner>

          <OrderDetails>
            <OrderDetailsHeader>
              <H3>Order details</H3>
              <ImportantLabel>
                <ExclamationCircleOutlined />
                <span>Important</span>
              </ImportantLabel>
            </OrderDetailsHeader>
            <BodyText>
              If you can&apos;t meet these specifications, please contact the
              venue to let them know
            </BodyText>
            {!isAdmin && (
              <ContactCustomerWrapper>
                <ContactCustomerLabel>Contact customer</ContactCustomerLabel>
                <ContactCustomerActions>
                  <ChatBox onClick={() => talkhandle()}>
                    {chatButton ? 'Message via OMS' : 'Open chat'}
                  </ChatBox>
                  {contactEmailAddress && (
                    <ContactLink
                      href={`mailto:${contactEmailAddress}`}
                      title={contactEmailAddress}
                    >
                      Email
                    </ContactLink>
                  )}
                  {contactPhoneNumber && (
                    <ContactLink href={`tel:${contactPhoneNumber}`}>
                      Call {contactPhoneNumber}
                    </ContactLink>
                  )}
                  <ChatBox onClick={copyContactDetails}>
                    {contactDetailsCopied ? 'Copied!' : 'Copy details'}
                  </ChatBox>
                  <ContactLink
                    href='/supplier/customers'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    View customer
                  </ContactLink>
                </ContactCustomerActions>
              </ContactCustomerWrapper>
            )}
            <OrdersDetailsText>Required delivery date</OrdersDetailsText>
            <OrderDetailsNotes>{requiredDeliveryDayToShow}</OrderDetailsNotes>
            {notes && notes.length > 0 && (
              <Fragment>
                <OrdersDetailsText>Order notes</OrdersDetailsText>
                <NotesWrapper>
                  {notes.map((note, index) => (
                    <OrderDetailsNotes key={index.toString()}>
                      {note.notes}
                    </OrderDetailsNotes>
                  ))}
                </NotesWrapper>
              </Fragment>
            )}
          </OrderDetails>

          {noApprovedOrders.length > 0 && (
            <InnerBody>
              <TableHeader>
                <SpaceBetween>
                  <H4>New orders on {displayDate}</H4>
                  <UnapprovedHeaderActions>
                    <DownloadButton onClick={downloadCsv}>
                      Download CSV
                    </DownloadButton>
                    <H4>
                      <Checkbox onChange={event => hanldeApproveAll(event)}>
                        Approve all
                      </Checkbox>
                    </H4>
                  </UnapprovedHeaderActions>
                </SpaceBetween>
                {orderNumber && orderNumber !== 'all' && (
                  <OrderNumber>
                    Purchase order # {decodedOrderNumber}
                  </OrderNumber>
                )}
                <BodyText>
                  Please let us know how many items you have in stock by editing
                  the delivery.
                </BodyText>
              </TableHeader>
              <Table
                scroll={{
                  scrollToFirstRowOnChange: true,
                }}
                dataSource={noApprovedOrders.map((order, index) => ({
                  key: order._id,
                  index: index,
                  ...order,
                }))}
                pagination={false}
                columns={TableColumnsCheckList(
                  receiptingState,
                  setReceiptingState,
                  editable,
                  isAdmin,
                  completed,
                  noApprovedOrders,
                  setNoApprovedOrders,
                  'noApproved',
                  locked,
                  layout,
                )}
              />
              {approvedOrders.length > 0 && (
                <RowTotalPrice>
                  <BodyText>Total of orders to approve: </BodyText>
                  <BodyText>£{noApprovedTotal}</BodyText>
                </RowTotalPrice>
              )}
            </InnerBody>
          )}

          {noApprovedOrders.length > 0 && approvedOrders.length > 0 && (
            <GreenDivider>
              <Divider />
            </GreenDivider>
          )}

          {approvedOrders.length > 0 && (
            <InnerBody>
              <TableHeader>
                <SpaceBetween>
                  <H4>Orders you approved on {displayDate}</H4>
                  <DownloadButton onClick={downloadCsv}>
                    Download CSV
                  </DownloadButton>
                </SpaceBetween>
                {orderNumber && orderNumber !== 'all' && (
                  <OrderNumber>Purchase order # {orderNumber}</OrderNumber>
                )}
                {isManuallyAdded && (
                  <ManuallyInvoice>
                    Order Placed Manually On the Dashboard
                  </ManuallyInvoice>
                )}
              </TableHeader>
              <Table
                scroll={{
                  scrollToFirstRowOnChange: true,
                }}
                dataSource={approvedOrders.map((order, index) => ({
                  key: order._id,
                  index: index,
                  ...order,
                }))}
                pagination={false}
                columns={TableColumnsCheckList(
                  receiptingState,
                  setReceiptingState,
                  editable,
                  isAdmin,
                  completed,
                  approvedOrders,
                  () => {},
                  'approved',
                  locked,
                  layout,
                  isSubstitutedColumnShown,
                )}
              />
              {noApprovedOrders.length > 0 && (
                <RowTotalPrice>
                  <BodyText>Total of orders you approved: </BodyText>
                  <BodyText>£{approvedTotal}</BodyText>
                </RowTotalPrice>
              )}
            </InnerBody>
          )}

          {noApprovedOrders.length > 0 && approvedOrders.length > 0 && (
            <GreenDivider>
              <Divider />
            </GreenDivider>
          )}

          <CSVLink
            ref={csvLink}
            data={csvData.current}
            filename={`orders-${title}-${displayDate}`}
            style={{ display: 'none' }}
            target='_self'
          ></CSVLink>

          <InnerBody>
            <OrderFinancialBreakdown
              orders={[...approvedOrders, ...noApprovedOrders]}
              context={{ venueId, supplierId }}
            />
            <RowTotalPrice total>
              <BodyText>
                <b>
                  {[...approvedOrders, ...noApprovedOrders].some(
                    line => line.orderFinancialSnapshot,
                  )
                    ? 'Goods ex VAT:'
                    : 'Total ex VAT:'}
                </b>
              </BodyText>
              <BodyText>
                <b>£{(+approvedTotal + +noApprovedTotal).toFixed(2)}</b>
              </BodyText>
            </RowTotalPrice>
            <RowTotalPrice total>
              <BodyText>
                <b>VAT Amount:</b>
              </BodyText>
              <BodyText>
                <b>£{(+vatTotalForAprov + +vatTotalForNoAprov).toFixed(2)}</b>
              </BodyText>
            </RowTotalPrice>

            <RowTotalPrice total>
              <BodyText>
                <b>
                  {[...approvedOrders, ...noApprovedOrders].some(
                    line => line.orderFinancialSnapshot,
                  )
                    ? 'Goods incl VAT'
                    : 'Total Amount'}
                </b>
              </BodyText>
              <BodyText>
                <b>
                  £{(+totalAmountForAprov + +totalAmountForNoAprov).toFixed(2)}
                </b>
              </BodyText>
            </RowTotalPrice>

            <RowButton>
              <Buttons>
                {isAdmin && completed && !editable && renderEditButton()}
                {isAdmin && completed && editable && renderSaveButton()}
                {completed ? (
                  renderApprovedButton()
                ) : !areEveryOrderConfirmed ? (
                  <Tooltip
                    placement='topRight'
                    title={
                      !areEveryOrderConfirmed
                        ? 'You need to accept or reject every product'
                        : ''
                    }
                  >
                    {renderApprovedButton()}
                  </Tooltip>
                ) : (
                  <Popconfirm
                    title='Are you sure you want to confirm this order?'
                    placement='topRight'
                    onConfirm={e => {
                      e.stopPropagation()
                      completeOrder()
                    }}
                    onCancel={e => {
                      e.stopPropagation()
                    }}
                    okText='Yes'
                    cancelText='No'
                  >
                    {renderApprovedButton()}
                  </Popconfirm>
                )}
                {orderError && noApprovedOrders.length > 0 && (
                  <ErrorMessage>
                    There has been an error accepting this order. Please contact
                    info@ordersmadesimple.com
                  </ErrorMessage>
                )}
              </Buttons>
            </RowButton>
            <RowRemainder>
              <OrdersDetailsTextRemainder>
                Required delivery date: {requiredDeliveryDayToShow}
              </OrdersDetailsTextRemainder>
            </RowRemainder>
          </InnerBody>

          <Footer>
            <Inner>
              <AddressRow>
                <LeftColumn>
                  <H4>Deliver to:</H4>
                  <p>
                    <BodyText>
                      <b>{title}</b>
                    </BodyText>
                    <BodyText>{addressLine1 && addressLine1}</BodyText>
                    <BodyText>{addressLine2 && addressLine2}</BodyText>
                    <BodyText>{addressCity && addressCity}</BodyText>
                    <BodyText>{addressPostCode && addressPostCode}</BodyText>
                    <BodyText>{phoneNumber && phoneNumber}</BodyText>
                  </p>
                </LeftColumn>
                {hasVenueUpdateAddress && (
                  <ErrorTag>
                    <ExclamationCircleOutlined
                      style={{
                        fontSize: 17,
                        paddingRight: 4,
                      }}
                    />
                    Delivery address has been updated
                  </ErrorTag>
                )}
              </AddressRow>

              {/* {venueDeliveryDays && renderVenueDeliveryDays()} */}

              <BodyText textAlign={'center'}>
                If you have any issues, please contact{' '}
                <a href='mailto:info@ordersmadesimple.com'>
                  info@ordersmadesimple.com
                </a>
              </BodyText>
            </Inner>
          </Footer>
        </Wrapper>
      )}
      <SalesAdvisorModal
        visible={salesAdvisorVisible}
        source='checklist'
        venue={{
          _id: venueId,
          venueId,
          title,
          venueTitle: title,
        }}
        supplierId={supplierId}
        supplierTitle={supplierTitle}
        checklist={{
          supplierId,
          date,
          purchaseOrderNumber: decodedOrderNumber,
          token: checklistToken,
        }}
        fallbackEmail={contactEmailAddress || ''}
        onClose={() => setSalesAdvisorVisible(false)}
      />
    </ChecklistAccessGate>
  )
}

const Wrapper = styled(FadeIn)`
  background-color: ${({ theme }) => theme.colours.mayoWhite};
`

const Inner = styled.div`
  width: 60%;
  margin: 0 auto;
  ${({ theme }) => theme.device('sm')`
      padding: 0 15px;
  `}
`

const Header = styled.header`
  background: ${({ theme }) => theme.colours.bbqBlack};
  padding: ${({ theme }) => `${theme.spacing.xl} 0`};
`

const VenueName = styled(H1)`
  line-height: ${({ theme }) => theme.spacing.xl};
  padding: 10px 0;
  color: ${({ theme }) => theme.colours.lettuceGreen};
  margin-bottom: 0;
`

const HeaderContent = styled(Inner)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LeftHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  color: ${({ theme }) => theme.colours.mayoWhite};
`

const Logo = styled.img`
  height: 108px;
`

const ErrorTag = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colours.bgGrey};
  border: 1px solid ${({ theme }) => theme.colours.chilliRed};
  color: ${({ theme }) => theme.colours.chilliRed};
  border-radius: 5px;
  padding: ${({ theme }) => theme.spacing.tiny};
`

const InnerBody = styled(Inner)`
  .ant-table-thead * {
    background-color: ${({ theme }) => theme.colours.mayoWhite};
    white-space: nowrap;
  }
  .restrictShrink {
    padding: 12px 3px;
    ${({ theme }) => theme.device('sm')`
      padding: 14px 4px;
    `}
    ${({ theme }) => theme.device('md')`
      padding: 16px 5px;
    `}
  }
  .restrictShrink:first-child {
    white-space: nowrap;
    padding-left: 0;
  }
  .restrictShrink:last-child {
    padding-right: 0;
  }
  .restrictShrink.quantity-padding-left {
    padding-left: 5px;
    ${({ theme }) => theme.device('sm')`
      padding-left: 10px;
    `}
  }
  .text-right {
    text-align: right;
  }
`

const OrderNumber = styled.div`
  font-family: 'Larsseit';
  font-style: normal;
  font-weight: bold;
  font-size: 1rem; // 16px
`

const ManuallyInvoice = styled.div`
  font-family: 'Larsseit';
  font-style: normal;
  font-weight: bold;
  color: ${({ theme }) => `${theme.colours.lettuceGreen} !important`};
  font-size: 1rem; // 16px
`

const TableHeader = styled.div`
  margin: ${({ theme }) => `${theme.spacing.xl} 0 ${theme.spacing.lg} 0`};
`

const GreenDivider = styled.div`
  background-color: ${({ theme }) =>
    `${theme.colours.lettuceGreen} !important`};
  max-width: 700px;
  margin: 0 auto;
`

const RowTotalPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${({ total, theme }) =>
    total ? `${theme.spacing.lg} 0 0 0` : `${theme.spacing.lg} 0`};
`

const Footer = styled.footer`
  background-color: ${({ theme }) => theme.colours.bbqBlack};
  color: ${({ theme }) => theme.colours.mayoWhite};
  padding: ${({ theme }) => `${theme.spacing.sm} 0`};
  text-align: center;
`

const AddressRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const LeftColumn = styled.div`
  p {
    display: flex;
    flex-direction: column;
  }
  H4 {
    text-align: left;
    color: white;
  }
`

// const DeliveryDaysRow = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: flex-start;
// `

const RowButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`
const RowRemainder = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: -30px;
  margin-bottom: 12px;
`

const Buttons = styled.div`
  width: 200px;
  margin: ${({ theme }) => `${theme.spacing.lg} 0 ${theme.spacing.xl} 0`};
`

const CenterScreen = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
`

const ApprovedButton = styled(LoadingButton)`
  cursor: ${props => props.completed && 'default'};
  background-color: ${props =>
    props.completed && `${props.theme.colours.seaweedGreen} !important`};
  color: ${props =>
    props.completed && `${props.theme.colours.mayoWhite} !important`};
`
const EditButton = styled(LoadingButton)`
  background-color: ${props =>
    `${props.theme.colours.lettuceGreen} !important`};
  color: ${props => `${props.theme.colours.mayoWhite} !important`};
  margin-bottom: 10px;
`
const DownloadButton = styled(LoadingButton)``

const UnapprovedHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const ErrorMessage = styled(H4)`
  padding-top: ${({ theme }) => `${theme.spacing.lg}`};
  color: ${({ theme }) => `${theme.colours.chilliRed}`};
`

const OrderDetails = styled(Inner)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: left;
  margin-top: 50px;
  border: 1.5px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 5px;
  padding: ${({ theme }) => theme.spacing.sm} !important;
`

const OrdersDetailsText = styled(H4)`
  color: ${({ theme }) => theme.colours.chilliRed};
  margin: 10px 0 5px 0;
`
const OrdersDetailsTextRemainder = styled(H4)`
  color: ${({ theme }) => theme.colours.chilliRed};
  margin: 10px 0 5px 0;
`

const OrderDetailsNotes = styled(BodyText)`
  font-weight: 700;
  width: fit-content;
`

const NotesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: left;
`

const OrderDetailsHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  h3 {
    margin: 0 0 5px 0;
  }
`

const ImportantLabel = styled.div`
  padding: 0 5px;
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colours.chilliRed};
  color: ${({ theme }) => theme.colours.chilliRed};
  border-radius: 5px;
  height: 30px;
  span {
    margin: 0 5px;
  }
`
const ChatBox = styled(Button)`
  && {
    width: fit-content;
    margin-top: 0;
    height: 32px;
    padding: 4px 15px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`

const ContactCustomerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const ContactCustomerLabel = styled(H4)`
  margin: 10px 0 0 0;
`

const ContactCustomerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xxs};
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`

const ContactLink = styled.a`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 4px 15px;
    box-sizing: border-box;
    font-size: 14px;
    border-radius: 2px;
    border: 1px solid ${({ theme }) => theme.colours.panSilver};
    color: ${({ theme }) => theme.colours.bbqBlack};
    text-decoration: none;
    &:hover {
      border-color: ${({ theme }) => theme.colours.bbqBlack};
      color: ${({ theme }) => theme.colours.bbqBlack};
    }
  }
`

const SalesAdvisorBanner = styled(Inner)`
  margin-top: 32px;
`

const SalesAdvisorBannerInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 55%, #ffffff 100%);
  border: 1.5px solid #7dd3fc;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(14, 165, 233, 0.12);
  ${({ theme }) => theme.device('sm')`
    flex-direction: column;
    align-items: flex-start;
  `}
`

const SalesAdvisorBannerCopy = styled.div`
  flex: 1;
  min-width: 0;
`

const SalesAdvisorHeadline = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #0c4a6e;
`

const SalesAdvisorBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #0369a1;
`

const SalesAdvisorCta = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 12px 22px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #38bdf8 0%, #0284c7 58%, #0369a1 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(14, 165, 233, 0.32);
  white-space: nowrap;

  &:hover {
    filter: brightness(1.06);
  }
`

// const VenueDeliveryDaysHolder = styled.div`
//   padding: ${({ theme }) => `${theme.spacing.xs} 0`};
//   text-align: left;
// `

// const DeliveryDays = styled.div`
//   display: flex;
//   flex-direction: row;
// `

// const DayBox = styled.div`
//   background-color: ${({ theme }) => theme.colours.avocadoGreen};
//   border-color: ${({ theme }) => theme.colours.mayoWhite};
//   margin: ${({ theme }) => `${theme.spacing.tiny}`};
//   font-weight: normal !important;
//   display: flex;
//   border: 1px solid #d9d9d9;
//   border-radius: 4px;
//   width: 60px;
//   justify-content: center;
// `

export default OrderChecklist
